import React, { useState } from 'react';
import { User, Lock, Layers, ChevronDown, AlertCircle } from 'lucide-react';
import Button from '../ui/Button';
import usePermissionStore from '../../stores/permissionStore';
import { UserRole } from '../../types/permission';
import { authService } from '../../services';

// 是否使用 API 认证模式
const USE_API_MODE = import.meta.env.VITE_USE_API_AUTH === 'true';

const LoginView = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState(UserRole.ADMIN);
  const [showRoleSelect, setShowRoleSelect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login, loginFromApi } = usePermissionStore();

  const roleOptions = [
    { value: UserRole.ADMIN, label: '管理员', description: '拥有所有权限' },
    { value: UserRole.TEACHER, label: '老师', description: '管理学员和课程' },
    { value: UserRole.STUDENT, label: '学生/家长', description: '查看自己的记录' },
  ];

  /**
   * 处理 API 登录
   */
 const handleApiLogin = async () => {
    if (!username.trim()) {
      setError('请输入用户名');
      return;
    }
    if (!password.trim()) {
      setError('请输入密码');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authService.login(username, password, false);
      
      // 使用后端返回的用户信息和权限
      // loginFromApi 函数内部会处理学生ID获取逻辑
      await loginFromApi(response);
      
      // 通知父组件登录成功
      onLogin();
    } catch (err) {
      console.error('登录失败:', err);
      // 根据错误类型显示友好的错误信息
      let errorMessage = '登录失败，请稍后重试';
      if (err.message) {
        // 将英文错误信息转换为中文
        if (err.message.includes('Invalid username or password') || 
            err.message.includes('Invalid credentials') ||
            err.message.includes('认证已过期') ||
            err.message.includes('3秒后将跳转')) {
          // 登录时的401错误都表示用户名或密码错误
          errorMessage = '用户名或密码错误';
        } else if (err.message.includes('Account is disabled')) {
          errorMessage = '账户已被禁用，请联系管理员';
        } else if (err.message.includes('Username and password are required')) {
          errorMessage = '请输入用户名和密码';
        } else if (err.message.includes('网络') || err.message.includes('Network')) {
          errorMessage = '网络连接失败，请检查网络';
        } else if (err.message.includes('超时') || err.message.includes('timeout')) {
          errorMessage = '请求超时，请稍后重试';
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 处理演示模式登录
   */
  const handleDemoLogin = () => {
    // 调用权限系统登录
    login({
      username: username || '演示用户',
      role: selectedRole,
    });
    
    // 调用父组件的登录回调
    onLogin();
  };

  /**
   * 统一登录处理
   */
  const handleLogin = () => {
    if (USE_API_MODE) {
      handleApiLogin();
    } else {
      handleDemoLogin();
    }
  };

  const selectedRoleInfo = roleOptions.find(r => r.value === selectedRole);

  return (
    <div className="flex flex-col h-full bg-white px-8 justify-center relative z-20">
      {/* Decorative Circles */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl -mr-20 -mt-20 opacity-50 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl -ml-20 -mb-20 opacity-50 pointer-events-none"></div>

      <div className="mb-10 text-center relative">
        {/* 动态呼吸光效 - 象征阳光 (Sum) */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-100 rounded-full blur-3xl opacity-40 animate-pulse pointer-events-none"></div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50 rounded-full blur-3xl opacity-30 pointer-events-none"></div>
        
        <div className="w-24 h-24 bg-white rounded-[2rem] mx-auto mb-6 flex items-center justify-center shadow-2xl shadow-blue-100/80 relative z-10 border border-white/50 p-1 transform transition-transform hover:rotate-3 duration-500">
           <img src="/isumarts-logo.png" alt="夏天艺术 Logo" className="w-full h-full object-contain rounded-[1.8rem]" />
        </div>
        
        <h1 className="text-3xl font-[900] text-[#2D3748] mb-1 tracking-tight">夏天艺术</h1>
        <p className="text-blue-500 text-xs font-black tracking-[0.3em] mb-3 opacity-80 uppercase">SumArts</p>
        
        {/* 明确的教师/管理员端标识 */}
        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50/60 border border-blue-100 rounded-full mb-6 relative z-10 backdrop-blur-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
          </span>
          <span className="text-[10px] font-black text-blue-600 tracking-widest uppercase">教师 / 管理员端</span>
        </div>
        
        {/* 核心理念展示 - Sum / Art / Sparkle */}
        <div className="flex justify-center gap-6 mb-8 relative z-10">
          <div className="flex flex-col items-center group">
            <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center mb-1.5 transition-transform group-hover:scale-110">
              <span className="text-xl">☀️</span>
            </div>
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Sum</span>
            <span className="text-[9px] text-gray-400 font-medium">阳光</span>
          </div>
          <div className="flex flex-col items-center group">
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mb-1.5 transition-transform group-hover:scale-110">
              <span className="text-xl">🎨</span>
            </div>
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Art</span>
            <span className="text-[9px] text-gray-400 font-medium">美感</span>
          </div>
          <div className="flex flex-col items-center group">
            <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center mb-1.5 transition-transform group-hover:scale-110">
              <span className="text-xl">✨</span>
            </div>
            <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Sparkle</span>
            <span className="text-[9px] text-gray-400 font-medium">灵气</span>
          </div>
        </div>

        <div className="relative">
          <p className="text-[#4A5568] text-sm italic font-semibold relative z-10">“一群有趣的灵儿，在艺术的阳光下，闪闪发光”</p>
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-48 h-2 bg-blue-100/50 -rotate-1 rounded-full"></div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
          <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="space-y-5 mb-8">
        <div>
           <label className="block text-xs font-bold text-[#4A5568] mb-1.5 ml-1">用户名</label>
           <div className="flex items-center bg-[#F7FAFC] border border-[#EDF2F7] rounded-xl px-4 py-3.5 focus-within:border-[#3182CE] focus-within:bg-white focus-within:shadow-sm transition-all duration-200 group">
              <User size={20} className="text-gray-400 mr-3 group-focus-within:text-[#3182CE]" />
              <input 
                type="text" 
                placeholder="请输入用户名" 
                className="bg-transparent flex-1 outline-none text-sm text-[#2D3748] placeholder-gray-400"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError(''); // 清除错误提示
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                disabled={loading}
              />
           </div>
        </div>
        <div>
           <label className="block text-xs font-bold text-[#4A5568] mb-1.5 ml-1">密码</label>
           <div className="flex items-center bg-[#F7FAFC] border border-[#EDF2F7] rounded-xl px-4 py-3.5 focus-within:border-[#3182CE] focus-within:bg-white focus-within:shadow-sm transition-all duration-200 group">
              <Lock size={20} className="text-gray-400 mr-3 group-focus-within:text-[#3182CE]" />
              <input 
                type="password" 
                placeholder="请输入密码" 
                className="bg-transparent flex-1 outline-none text-sm text-[#2D3748] placeholder-gray-400"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(''); // 清除错误提示
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                disabled={loading}
              />
           </div>
        </div>
        
        {/* 角色选择 - 仅演示模式显示 */}
        {!USE_API_MODE && (
          <div>
            <label className="block text-xs font-bold text-[#4A5568] mb-1.5 ml-1">
              登录角色 <span className="text-gray-400 font-normal">(演示)</span>
            </label>
            <div className="relative">
              <div 
                className="flex items-center justify-between bg-[#F7FAFC] border-[#EDF2F7] rounded-xl px-4 py-3.5 cursor-pointer hover:border-[#3182CE] transition-all duration-200"
                onClick={() => setShowRoleSelect(!showRoleSelect)}
              >
                <div>
                  <span className="text-sm text-[#2D3748] font-medium">{selectedRoleInfo?.label}</span>
                  <span className="text-xs text-[#718096] ml-2">{selectedRoleInfo?.description}</span>
                </div>
                <ChevronDown size={20} className={`text-gray-400 transition-transform ${showRoleSelect ? 'rotate-180' : ''}`} />
              </div>
              
              {showRoleSelect && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#EDF2F7] rounded-xl shadow-lg overflow-hidden z-10">
                    {roleOptions.map((role) => (
                      <div 
                        key={role.value}
                        className={`px-4 py-3 cursor-pointer hover:bg-[#F7FAFC] transition-colors ${selectedRole === role.value ? 'bg-blue-50' : ''}`}
                        onClick={() => {
                          setSelectedRole(role.value);
                          setShowRoleSelect(false);
                        }}
                      >
                        <span className="text-sm text-[#2D3748] font-medium">{role.label}</span>
                        <span className="text-xs text-[#718096] ml-2">{role.description}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <Button 
        variant="primary" 
        size="large" 
        className="w-full h-12 text-lg font-bold shadow-xl shadow-blue-200 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#3182CE] to-[#4299E1] border-none" 
        onClick={handleLogin}
        disabled={loading}
      >
        {loading ? '正在进入...' : '登 录'}
      </Button>
      
      <div className="mt-8 text-center">
        <p className="text-xs text-[#CBD5E0]">
          {USE_API_MODE ? '忘记密码请联系管理员' : '演示模式无需密码，选择角色后点击登录'}
        </p>
      </div>
    </div>
  );
};

export default LoginView;
